import React, { useRef, useLayoutEffect, useState, useMemo, useId } from "react";
import { useTranslation } from "react-i18next";
import "./SentimentWidget.css";

// Map a data-supplied sentiment label ("bullish"/"bearish"/"neutral") or a raw
// score to its translated, upper-cased word. Falls back to upper-casing the raw
// label for any value not in the set.
const LABEL_KEY = { bullish: "widget.bullish", bearish: "widget.bearish", neutral: "widget.neutral" };
function tLabel(tr, label) {
  return LABEL_KEY[label] ? tr(LABEL_KEY[label]) : (label || "").toUpperCase();
}
function tScoreLabel(tr, score) {
  return tr(score > 0.15 ? "widget.bullish" : score < -0.15 ? "widget.bearish" : "widget.neutral");
}

// ── Gauge helpers ─────────────────────────────────────────────────────────────

function polar(cx, cy, r, angleDeg) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
}

function scoreToAngle(score) {
  return 90 * (1 - Math.max(-1, Math.min(1, score)));
}

function arcPath(cx, cy, r, startScore, endScore) {
  const p1 = polar(cx, cy, r, scoreToAngle(startScore));
  const p2 = polar(cx, cy, r, scoreToAngle(endScore));
  const da = Math.abs(scoreToAngle(startScore) - scoreToAngle(endScore));
  const lg = da >= 180 ? 1 : 0;
  return `M ${p1.x.toFixed(1)} ${p1.y.toFixed(1)} A ${r} ${r} 0 ${lg} 1 ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
}

function labelColor(label) {
  if (label === "bullish") return "#22c55e";
  if (label === "bearish") return "#ef4444";
  return "#eab308";
}

function Gauge({ score, label, windowName, confidence }) {
  const { t: tr } = useTranslation();
  const cx = 100, cy = 100, r = 72, sw = 13;
  const np  = polar(cx, cy, r - 14, scoreToAngle(score));
  const col = labelColor(label);

  return (
    <div className="sw-gauge">
      <div className="sw-gauge-window">{windowName}</div>
      <svg viewBox="0 0 200 145" className="sw-gauge-svg" aria-hidden="true">
        <path d={arcPath(cx, cy, r, -1,    -0.15)} fill="none" stroke="#7f1d1d" strokeWidth={sw} strokeLinecap="butt" />
        <path d={arcPath(cx, cy, r, -0.15,  0.15)} fill="none" stroke="#713f12" strokeWidth={sw} strokeLinecap="butt" />
        <path d={arcPath(cx, cy, r,  0.15,  1   )} fill="none" stroke="#14532d" strokeWidth={sw} strokeLinecap="butt" />
        <path d={arcPath(cx, cy, r - 1, -1,    -0.15)} fill="none" stroke="#ef444455" strokeWidth={sw - 6} strokeLinecap="butt" />
        <path d={arcPath(cx, cy, r - 1, -0.15,  0.15)} fill="none" stroke="#eab30855" strokeWidth={sw - 6} strokeLinecap="butt" />
        <path d={arcPath(cx, cy, r - 1,  0.15,  1   )} fill="none" stroke="#22c55e55" strokeWidth={sw - 6} strokeLinecap="butt" />
        <line x1={cx} y1={cy} x2={np.x.toFixed(1)} y2={np.y.toFixed(1)}
              stroke={col} strokeWidth="3" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="5" fill={col} />
        <text x={cx} y={cy + 20} textAnchor="middle" fill={col} fontSize="15" fontWeight="bold">
          {score >= 0 ? "+" : ""}{score.toFixed(3)}
        </text>
        <text x={cx} y={cy + 34} textAnchor="middle" fill={col} fontSize="11">
          {tLabel(tr, label)}
        </text>
        <text x="18"  y="110" fill="#6b7280" fontSize="9" textAnchor="middle">{tr("widget.bear")}</text>
        <text x="182" y="110" fill="#6b7280" fontSize="9" textAnchor="middle">{tr("widget.bull")}</text>
      </svg>
    </div>
  );
}

// ── Direction indicator ───────────────────────────────────────────────────────

function DirectionIndicator({ current, previous, windowName }) {
  const { t: tr } = useTranslation();
  if (previous === null || previous === undefined) return null;
  const delta = current - previous;
  const thresh = 0.03;
  let arrow, col;
  if (delta > thresh)       { arrow = "▲"; col = "#22c55e"; }
  else if (delta < -thresh) { arrow = "▼"; col = "#ef4444"; }
  else                      { arrow = "→"; col = "#6b7280"; }

  return (
    <div className="sw-direction">
      <div className="sw-direction-label">{windowName} {tr("widget.vsPrev")}</div>
      <div className="sw-direction-arrow" style={{ color: col }}>{arrow}</div>
      <div className="sw-direction-delta" style={{ color: col }}>
        {delta >= 0 ? "+" : ""}{delta.toFixed(3)}
      </div>
    </div>
  );
}

// ── Combined price + sentiment SVG chart ──────────────────────────────────────

const fmtPrice = v => {
  if (v >= 10000) return v.toFixed(0);
  if (v >= 100)   return v.toFixed(1);
  if (v >= 10)    return v.toFixed(2);
  if (v >= 1)     return v.toFixed(4);
  return v.toFixed(5);
};

const TODAY = new Date().toISOString().slice(0, 10);

/**
 * Trade overlay for a selected strategy.
 *
 * Drawn in the price pane using the caller's own xOf/pY scales so it lines up
 * with the candles exactly. Per trade:
 *   entry  — triangle, pointing up for a long and down for a short
 *   exit   — square
 *   link   — dotted line between the two
 * A still-open trade runs its dotted line from entry to the latest close and is
 * marked with a hollow ring instead of a square, with its floating R labelled.
 *
 * Trades that opened before the first candle on screen cannot be placed on the
 * x-axis; they are counted and reported by the caller rather than silently
 * dropped, since otherwise a strategy's headline R would not match what is drawn.
 */
function TradeOverlay({ trades, dateIndex, xOf, pY, lastClose, rightEdge, onTradeHover }) {
  // Per-instance, for the same reason as clipId — three charts share this DOM.
  const glowId = "tradeGlow-" + useId().replace(/[^a-zA-Z0-9]/g, "");
  if (!trades?.length) return null;
  const WIN = "#16a34a";
  const LOSS = "#dc2626";
  const OPEN = "#2563eb";

  return (
    <g className="trade-overlay">
      <defs>
        <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="0" stdDeviation="2.2" floodOpacity="0.45" />
        </filter>
      </defs>
      {trades.map((t, k) => {
        const i0 = dateIndex[t.open_date];
        if (i0 == null) return null;                 // opened before the visible window
        const i1 = t.open ? dateIndex.__last : dateIndex[t.close_date];
        if (i1 == null) return null;
        const x0 = xOf(i0);
        const x1 = xOf(i1);
        const y0 = pY(t.entry);
        const y1 = pY(t.open ? lastClose : t.exit);
        const col = t.open ? OPEN : (t.R >= 0 ? WIN : LOSS);
        const up = t.side === "buy";
        const s = 5;
        const tri = up
          ? `${x0},${y0 - s - 2} ${x0 - s},${y0 + 2} ${x0 + s},${y0 + 2}`
          : `${x0},${y0 + s + 2} ${x0 - s},${y0 - 2} ${x0 + s},${y0 - 2}`;
        return (
          <g key={k} className="trade-mark" style={{ animationDelay: `${Math.min(k, 12) * 28}ms` }}>
            {/* White halo first: a same-day trade is a near-vertical hairline
                and was getting lost among the signal triangles. */}
            <line x1={x0} y1={y0} x2={x1} y2={y1}
                  stroke="#fff" strokeWidth={4} opacity={0.9} strokeLinecap="round" />
            <line x1={x0} y1={y0} x2={x1} y2={y1}
                  stroke={col} strokeWidth={2} strokeDasharray="4 3" opacity={0.95} />
            <polygon points={tri} fill={col} filter={`url(#${glowId})`}
                     stroke="#fff" strokeWidth={0.8} />
            {/* Sequence number: short and same-day trades are otherwise
                impossible to count against the candles. Neutral dark grey so it
                reads as a label rather than as more win/loss colour, and it stays
                legible over both green and red segments. */}
            {t.open ? (
              <>
                <circle cx={x1} cy={y1} r={4.5} fill="#fff" stroke={col} strokeWidth={2} />
                {/* An open trade sits on the newest bar, i.e. hard against the
                    right edge, so the label would be clipped by the SVG bounds.
                    Flip it to the left of the ring when there is no room. */}
                {(() => {
                  const label = `${t.R >= 0 ? "+" : ""}${t.R.toFixed(2)}R`;
                  const w = label.length * 5.6 + 8;
                  const flip = rightEdge != null && x1 + w > rightEdge;
                  return (
                    <text x={flip ? x1 - 7 : x1 + 7} y={y1 + 3.5}
                          textAnchor={flip ? "end" : "start"}
                          fill={col} fontSize={9.5} fontWeight={700}>
                      {label}
                    </text>
                  );
                })()}
              </>
            ) : (
              <rect x={x1 - 4.5} y={y1 - 4.5} width={9} height={9} fill={col}
                    stroke="#fff" strokeWidth={0.8} filter={`url(#${glowId})`} />
            )}

            {/* Badge LAST so the exit marker cannot paint over it, and placed on
                the opposite side of the entry from the exit whenever the two
                share a bar (a same-day trade) — that collision hid trades 2, 3
                and 7 entirely. */}
            {(() => {
              const sameBar = Math.abs(x1 - x0) < 12;
              const above = sameBar ? (y1 >= y0) : up;
              const by = above ? y0 - 12 : y0 + 20;
              return (
                <g className="trade-badge" style={{ cursor: "pointer", "--tb": col }}
                   onMouseEnter={() => onTradeHover && onTradeHover({ trade: t, index: k, x: x0, y: by })}
                   onMouseLeave={() => onTradeHover && onTradeHover(null)}>
                  {/* Glow disc — driven by CSS :hover rather than React state so
                      it cannot lag behind the cursor on re-render. Tinted with
                      the trade's own colour via the --tb custom property. */}
                  <circle className="trade-badge-glow" cx={x0} cy={by - 3.5} r={10} />
                  {/* Invisible disc gives the 9px glyph a usable hit area. */}
                  <circle cx={x0} cy={by - 3.5} r={10} fill="transparent" />
                  <text x={x0} y={by} textAnchor="middle"
                        fontSize={9.5} fontWeight={700} fill="#374151"
                        stroke="#fff" strokeWidth={2.6} paintOrder="stroke">
                    {k + 1}
                  </text>
                </g>
              );
            })()}
          </g>
        );
      })}
    </g>
  );
}

function CombinedChart({ candles, history, thresh = 0.15, selectedIdea = null,
                        xLabel = (d) => d.slice(5), priceTitle = null,
                        slotDivisor = null }) {
  const { t: tr } = useTranslation();
  const wrapRef   = useRef(null);
  const scrollRef = useRef(null);
  const [width, setWidth]       = useState(700);
  const [hoverIdx, setHoverIdx] = useState(null);
  // Unique per chart instance. This id was hardcoded "pricePaneClip"; once the
  // page rendered three charts (H1/H4/D1) all three emitted the same id, and
  // every one resolved url(#pricePaneClip) to the FIRST in document order —
  // H1's. H1 fits without scrolling, so its clip rect is only availW wide, and
  // it silently amputated every D1 candle past that x: Solana lost 10 bars (the
  // whole 08-18 rally), Bitcoin lost 7. The bars were in the DOM with correct
  // geometry throughout, which is why DOM-based checks kept passing.
  const uid    = useId().replace(/[^a-zA-Z0-9]/g, "");
  const clipId = "pricePaneClip-" + uid;
  const glowId = "liveGlow-" + uid;
  const [hoverTrade, setHoverTrade] = useState(null);
  const [scrollX, setScrollX] = useState(0);

  useLayoutEffect(() => {
    if (!wrapRef.current) return;
    // Measure immediately: relying on the observer's first callback left the
    // 700px default in place on a 506px container, so every derived width
    // (plot, track, thumb) was computed against a container that never existed.
    setWidth(Math.floor(wrapRef.current.clientWidth));
    const ro = new ResizeObserver(e => setWidth(Math.floor(e[0].contentRect.width)));
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  // Date axis = the candle sequence, plus any scored bars newer than the last
  // candle (the prediction slots on the right).
  //
  // Deliberately NOT the full union with history. Sentiment is scored around
  // the clock, but most instruments stop trading overnight and at weekends, so
  // the union puts interior slots on the axis that no candle can ever fill —
  // one empty slot per market closure, read as a hole punched in the price
  // series. Gold/US500/Natural Gas H1 each showed one; Bitcoin, trading 24/7,
  // showed none, which is what identified the cause.
  //
  // Sentiment scored while the market was shut is still in `history`; it simply
  // has no price bar to sit above, so the line spans the closure instead of
  // reserving dead width for it.
  const allDates = useMemo(() => {
    const cd = candles.map(c => c.date);
    const lastCandle = cd.length ? cd[cd.length - 1] : null;
    const trailing = history
      .map(h => h.date)
      .filter(d => lastCandle === null || d > lastCandle);
    return [...new Set([...cd, ...trailing])].sort();
  }, [candles, history]);

  const candleMap = useMemo(() => Object.fromEntries(candles.map(c => [c.date, c])), [candles]);
  const histMap   = useMemo(() => Object.fromEntries(history.map(h => [h.date, h])), [history]);

  // date -> x-axis slot, for placing trade markers. __last is the right edge,
  // which is where a still-open trade's dotted line terminates.
  const dateIndex = useMemo(() => {
    const m = {};
    allDates.forEach((d, i) => { m[d] = i; });
    m.__last = allDates.length - 1;
    return m;
  }, [allDates]);

  const lastClose = useMemo(() => {
    const withClose = candles.filter(c => c.close != null);
    return withClose.length ? withClose[withClose.length - 1].close : null;
  }, [candles]);

  // ── Layout ──
  // The Y axes live in their own fixed-width SVG to the left; the plot lives in
  // a horizontally scrollable SVG beside it. Both panes are in that ONE plot
  // SVG sharing a single x scale, so price and sentiment scroll together by
  // construction — there is no scroll synchronisation to get wrong.
  const AXIS_W   = 56;
  const MR       = 12;
  const BAR_PX   = 13;      // fixed per-bar width; what makes the chart scroll
  const GUTTER   = 40;      // right-hand breathing room; holds the live marker
  const TITLE_H  = 18;
  const CANDLE_H = 190;
  const AXIS_H   = 22;
  const GAP      = 20;   // leaves room for the scrollbar under the price pane
  const STITLE_H = 16;
  const SENT_H   = 180;
  const MB       = 4;

  const candleTop = TITLE_H;
  const axisTop   = candleTop + CANDLE_H;
  const sTitleTop = axisTop + AXIS_H + GAP;
  const sentTop   = sTitleTop + STITLE_H;
  const totalH    = sentTop + SENT_H + MB;

  const n      = allDates.length || 1;
  // availW must equal the scroller's OWN width. MR reserved a right margin the
  // flex layout never applied, so a chart that fits without scrolling (D1) ended
  // up 10px narrower than its container and its marker sat out of line with the
  // scrolling charts, whose right edge coincides with the container's.
  const availW = Math.max(width - AXIS_W, 100);
  // Fit to the container while the history is short; grow (and scroll) once it
  // no longer fits, so candles keep a readable width instead of being squeezed.
  // The newest score can belong to a period that has not closed yet, so it has
  // no candle. It is drawn inside the right-hand gutter rather than as another
  // bar: the whitespace itself says "this has not happened", and it keeps the
  // live marker clear of the completed price action.
  // Every TRAILING scored bar that has no candle yet, not just the newest one.
  // Sentiment is scored on the hour; MT5 candles can lag by more than one bar
  // (and do over weekends), so H1 regularly carries two scored-but-uncandled
  // bars. Reserving a slot for only the last one left the earlier one holding
  // an empty slot mid-series — the visible gap between the final candle and the
  // marker.
  let nPredict = 0;
  while (nPredict < n
         && !candleMap[allDates[n - 1 - nPredict]]
         && !!histMap[allDates[n - 1 - nPredict]]) {
    nPredict++;
  }
  const hasPrediction = nPredict > 0;
  const nBars    = n - nPredict;

  // Two regimes, one invariant. If the bars fit the container, STRETCH the
  // slots so they fill it (no scroll, no dead space); if not, fix them at
  // BAR_PX and scroll. Either way the newest slot ends at availW - GUTTER, so
  // the live marker sits at the same screen x on every chart at every window
  // width. The previous right-aligned fixed-width scheme left a container-wide
  // void to the LEFT of short charts on wide screens — D1 with 79 bars on a
  // 2400px window drew half a screen of nothing before the first candle.
  // Gutter grows to fit however many prediction slots there are, so the bars
  // never have to give up a slot to them.
  const predW  = GUTTER + Math.max(0, nPredict - 1) * BAR_PX;
  const fits   = nBars * BAR_PX + predW <= availW;
  // Stretch is capped: a dozen bars on a wide screen would otherwise sit in
  // ~50px slots, and with the body width previously pinned at 11px the candles
  // floated "miles apart". Bodies now scale with the slot (62%, capped so they
  // stay candles rather than slabs); any remainder from the cap folds in as
  // left padding, so the newest slot still ends at availW - GUTTER and the
  // marker keeps its fixed screen position.
  const SLOT_MAX = 44;
  // slotDivisor pins this chart's slot width to another chart's grid: the
  // intraday charts pass D1's slot count so H1/H4 candles render at exactly
  // the size and rhythm of the daily ones, right-aligned against the marker,
  // instead of a handful of bars stretched "miles apart" across the width.
  const slotW = slotDivisor
    ? Math.min((availW - predW) / Math.max(slotDivisor, 1), SLOT_MAX)
    : (fits ? Math.min((availW - predW) / Math.max(nBars, 1), SLOT_MAX) : BAR_PX);
  const shortOfWidth = nBars * slotW + predW <= availW;
  const leftPad  = shortOfWidth ? Math.max(0, availW - predW - nBars * slotW) : 0;
  const contentW = leftPad + nBars * slotW;
  const plotW  = contentW + predW > availW ? contentW + predW : availW;
  const barW   = Math.max(Math.min(slotW * 0.62, 26), 1);
  // The prediction is the NEXT bar, so it sits exactly one slot-step past the
  // last candle — mid-gutter put it ~2 steps out and read as a missing bar.
  const xOf    = i => (hasPrediction && i >= nBars)
    ? contentW + Math.min(slotW, GUTTER) / 2 + (i - (n - 1)) * BAR_PX
    : leftPad + (i + 0.5) * slotW;
  const R      = plotW;
  const scrolls = plotW > availW + 1;

  // Open at the newest bars — the right edge is the interesting end.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = el.scrollWidth;
    // Setting scrollLeft programmatically does not fire onScroll here, so the
    // thumb would sit at 0% while the chart was scrolled fully right.
    setScrollX(el.scrollLeft);
  }, [plotW, n]);

  // ── Price axis — scaled to the VISIBLE window ──
  // Fixed over all bars, off-screen history set the scale: GOLD H1's full range
  // was 372 points while the bars on screen spanned 128, so the visible candles
  // sat squashed in a third of the pane. Rescaling to what is actually in view
  // lets the bars use the full height; the axis re-labels as you scroll.
  const completedCandles = candles.filter(c => c.date !== TODAY);
  const visFrom = Math.max(0, Math.floor(scrollX / slotW));
  const visTo   = Math.min(nBars, Math.ceil((scrollX + availW) / slotW) + 1);
  const visible = allDates.slice(visFrom, visTo)
    .map(dte => candleMap[dte]).filter(c => c && c.date !== TODAY);
  const basis  = visible.length ? visible : completedCandles;
  const prices = basis.flatMap(c => [c.low, c.high]);
  const rawMin = prices.length ? Math.min(...prices) : 0;
  const rawMax = prices.length ? Math.max(...prices) : 1;
  const padP   = (rawMax - rawMin) * 0.06 || 0.01;
  const pMin   = rawMin - padP;
  const pMax   = rawMax + padP;
  const pY     = p => candleTop + CANDLE_H * (1 - (p - pMin) / (pMax - pMin));
  const pTicks = Array.from({ length: 5 }, (_, i) => pMin + (i / 4) * (pMax - pMin));

  // ── Sentiment axis — auto-scaled to data range ──
  const allScores = history.flatMap(h => [h.score_d7, h.score_d3]).filter(v => v != null);
  const sRawMin = allScores.length ? Math.min(...allScores) : -0.2;
  const sRawMax = allScores.length ? Math.max(...allScores) : 0.2;
  const sPad    = Math.max((sRawMax - sRawMin) * 0.25, 0.05);
  const sMin    = Math.max(-1, sRawMin - sPad);
  const sMax    = Math.min(1,  sRawMax + sPad);
  const sRange  = sMax - sMin || 1;
  const sY      = s => sentTop + SENT_H * (1 - (s - sMin) / sRange);
  const sTicks  = Array.from({ length: 5 }, (_, i) => sMin + (i / 4) * sRange);

  // ── X-axis ticks ──
  // Thin labels by SLOT width, not plot width: a right-aligned short chart
  // (D1-sized slots, few bars) otherwise computes "plenty of room" from the
  // full container and prints every label into a 200px clump.
  const labelStep = Math.max(1, Math.ceil(46 / slotW));
  const xTicks = [];
  for (let i = 0; i < n; i += labelStep) xTicks.push(i);

  // ── Sentiment line paths ──
  // Connect ACROSS bars that have no score instead of breaking the line.
  // Intraday, scores are far sparser than candles (a handful of scored bars
  // among 168 hourly ones), so breaking on every gap turned each point into an
  // isolated move-to with no line segment at all — the chart rendered as a few
  // detached dashes. D1 has a score per candle so it never hit this.
  const buildPath = pts => {
    let d = '', pen = false;
    for (const [x, v] of pts) {
      if (v == null) continue;
      const y = sY(v);
      d += pen ? ` L ${x.toFixed(1)} ${y.toFixed(1)}` : `M ${x.toFixed(1)} ${y.toFixed(1)}`;
      pen = true;
    }
    return d;
  };

  const d7Path = buildPath(allDates.map((d, i) => [xOf(i), histMap[d]?.score_d7 ?? null]));
  const d3Path = buildPath(allDates.map((d, i) => [xOf(i), histMap[d]?.score_d3 ?? null]));

  // The newest bar we actually have a score for. TODAY was a date string and
  // never matched an intraday ISO timestamp, so the marker simply vanished on
  // the H1/H4 charts. Keyed off the data instead: if scoring has lagged, the
  // marker sits where the last real score is rather than implying freshness.
  const latestScoredIdx = useMemo(() => {
    for (let i = allDates.length - 1; i >= 0; i--) {
      if (histMap[allDates[i]]) return i;
    }
    return -1;
  }, [allDates, histMap]);

  // ── Hover handlers ──
  // getBoundingClientRect() on the plot SVG already accounts for scrollLeft,
  // so no scroll offset maths is needed here.
  const handleMouseMove = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    const rel = e.clientX - rect.left;
    // Mirror xOf exactly: bars start after leftPad on right-aligned charts.
    // Without the subtraction the computed index ran past n on H1/H4 and the
    // tooltip + crosshair simply never appeared there.
    // In the gutter, pick the nearest prediction slot (there can be several).
    const i = rel >= contentW && hasPrediction
      ? Math.min(n - 1, nBars + Math.max(0, Math.floor((rel - contentW) / BAR_PX)))
      : Math.floor((rel - leftPad) / slotW);
    setHoverIdx(i >= 0 && i < n ? i : null);
  };

  // ── Tooltip ──
  const TTIP_W    = 155;
  const TTIP_ROW  = 14;
  let ttipEl = null;

  if (hoverIdx != null && !hoverTrade) {
    const date = allDates[hoverIdx];
    const c    = date !== TODAY ? candleMap[date] : null;
    const h    = histMap[date];
    const rows = [];

    if (c) {
      const col = c.close >= c.open ? "#16a34a" : "#dc2626";
      rows.push({ label: "O", val: fmtPrice(c.open),  col });
      rows.push({ label: "H", val: fmtPrice(c.high),  col });
      rows.push({ label: "L", val: fmtPrice(c.low),   col });
      rows.push({ label: "C", val: fmtPrice(c.close), col });
    }
    if (h) {
      const sign = v => (v >= 0 ? "+" : "") + v.toFixed(3);
      rows.push({ label: tr("widget.trend"), val: `${sign(h.score_d7)} ${h.label_d7 ? tLabel(tr, h.label_d7) : tScoreLabel(tr, h.score_d7)}`, col: "#3b82f6" });
      rows.push({ label: tr("widget.momentum"),  val: `${sign(h.score_d3)} ${h.label_d3 ? tLabel(tr, h.label_d3) : tScoreLabel(tr, h.score_d3)}`, col: "#f97316" });
    }

    if (rows.length > 0) {
      const TTIP_H = 20 + rows.length * TTIP_ROW + 6;
      let ttx = xOf(hoverIdx) + 10;
      if (ttx + TTIP_W > R - 4) ttx = xOf(hoverIdx) - TTIP_W - 10;
      const tty = candleTop + 6;

      ttipEl = (
        <g pointerEvents="none">
          <line x1={xOf(hoverIdx).toFixed(1)} y1={candleTop}
                x2={xOf(hoverIdx).toFixed(1)} y2={sentTop + SENT_H}
                stroke="#9ca3af" strokeWidth={1} strokeDasharray="2 2" />
          <rect x={ttx} y={tty} width={TTIP_W} height={TTIP_H}
                fill="white" stroke="#d1d5db" strokeWidth={1} rx={4} />
          <text x={ttx + 8} y={tty + 14} fontSize={11} fontWeight="600" fill="#111827">
            {/* Intraday keys are ISO timestamps; raw they render as
                "27T02:00:00+00:00". Show them in the viewer's local zone,
                matching the axis. Daily keys stay as plain dates. */}
            {date.length > 10 ? fmtLocalTs(date) : date}
          </text>
          {rows.map((r, i) => (
            <g key={i}>
              <text x={ttx + 8}          y={tty + 20 + (i + 1) * TTIP_ROW}
                    fontSize={10} fill="#6b7280">{r.label}</text>
              <text x={ttx + TTIP_W - 8} y={tty + 20 + (i + 1) * TTIP_ROW}
                    textAnchor="end" fontSize={10} fill={r.col}>{r.val}</text>
            </g>
          ))}
        </g>
      );
    }
  }

  // ── Trade detail tooltip (hovering a numbered badge) ──
  let tradeTipEl = null;
  if (hoverTrade) {
    const t = hoverTrade.trade;
    const days = Math.round(
      (new Date(t.close_date) - new Date(t.open_date)) / 86400000);
    const REASON = {
      sltp: tr("widget.reasonSltp"),
      eod:  tr("widget.reasonEod"),
      reversal: tr("widget.reasonReversal"),
      time: tr("widget.reasonTime"),
    };
    const held = days === 0
      ? tr("widget.sameDay")
      : (days === 1 ? tr("widget.dayOne", { n: days }) : tr("widget.dayOther", { n: days }));
    const rows = [
      [tr("widget.direction"), t.side === "buy" ? tr("widget.long") : tr("widget.short")],
      [tr("widget.opened"),    `${t.open_date}  @ ${fmtPrice(t.entry)}`],
      [t.open ? tr("widget.now") : tr("widget.closed"),
       t.open ? `${fmtPrice(lastClose)}  (${tr("widget.open")})` : `${t.close_date}  @ ${fmtPrice(t.exit)}`],
      [tr("widget.held"),      held],
      [tr("widget.result"),    `${t.R >= 0 ? "+" : ""}${t.R.toFixed(2)}R${t.open ? ` (${tr("widget.floating")})` : ""}`],
      [tr("widget.exit"),      REASON[t.reason] || t.reason || "—"],
    ];
    const TW = 208, TR = 15;
    const TH = 22 + rows.length * TR + 6;
    let tx = hoverTrade.x + 14;
    if (tx + TW > R - 4) tx = hoverTrade.x - TW - 14;
    if (tx < 2) tx = 2;
    let ty = hoverTrade.y - TH / 2;
    ty = Math.max(candleTop + 2, Math.min(ty, sentTop - TH - 2));
    const rc = t.open ? "#2563eb" : (t.R >= 0 ? "#16a34a" : "#dc2626");

    tradeTipEl = (
      <g pointerEvents="none">
        <rect x={tx} y={ty} width={TW} height={TH} rx={5}
              fill="#fff" stroke="#d1d5db" strokeWidth={1} />
        <text x={tx + 9} y={ty + 15} fontSize={11} fontWeight={700} fill="#111827">
          {tr("widget.tradeN", { n: hoverTrade.index + 1 })}
        </text>
        <text x={tx + TW - 9} y={ty + 15} textAnchor="end"
              fontSize={11} fontWeight={700} fill={rc}>
          {`${t.R >= 0 ? "+" : ""}${t.R.toFixed(2)}R`}
        </text>
        {rows.map((r, i) => (
          <g key={i}>
            <text x={tx + 9} y={ty + 22 + (i + 1) * TR}
                  fontSize={10} fill="#6b7280">{r[0]}</text>
            <text x={tx + TW - 9} y={ty + 22 + (i + 1) * TR} textAnchor="end"
                  fontSize={10} fill="#374151">{r[1]}</text>
          </g>
        ))}
      </g>
    );
  }

  // ── Custom horizontal scrollbar, sat directly under the price pane ──
  // The native one belongs to the scroll container, which wraps BOTH panes, so
  // it renders below the sentiment chart where it is easy to miss entirely —
  // the chart just looked truncated. This drives the same scrollLeft.
  // Read the scroller itself rather than recomputing its size: any drift
  // between the assumed and actual container width produced a thumb wider than
  // its own track. Percentages keep it correct without measuring the track.
  const el        = scrollRef.current;
  const viewW     = el ? el.clientWidth : availW;
  const scrollW   = el ? el.scrollWidth : plotW;   // not contentW: that is the bar span
  const maxScroll = Math.max(scrollW - viewW, 1);
  const thumbPct  = Math.min(100, Math.max(6, (viewW / scrollW) * 100));
  const thumbPos  = (scrollX / maxScroll) * (100 - thumbPct);

  const startDrag = e => {
    e.preventDefault();
    const el = scrollRef.current;
    if (!el) return;
    const startClientX = e.clientX;
    const startScroll  = el.scrollLeft;
    const trackPx = e.currentTarget.parentElement.getBoundingClientRect().width;
    const thumbPx = trackPx * (thumbPct / 100);
    const move = ev => {
      const dx = ev.clientX - startClientX;
      el.scrollLeft = startScroll + dx * (maxScroll / Math.max(trackPx - thumbPx, 1));
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  // Click anywhere on the track to jump there.
  const jumpTo = e => {
    const el = scrollRef.current;
    if (!el) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const thumbPx = rect.width * (thumbPct / 100);
    const frac = (e.clientX - rect.left - thumbPx / 2) / Math.max(rect.width - thumbPx, 1);
    el.scrollLeft = Math.max(0, Math.min(1, frac)) * maxScroll;
  };

  const titleStyle = {
    position: "absolute", left: AXIS_W, right: 0, textAlign: "center",
    fontSize: 10, color: "#6b7280", letterSpacing: "0.08em",
    pointerEvents: "none",
  };

  return (
    <div ref={wrapRef} style={{ width: "100%", position: "relative" }}>
      {/* Titles sit outside the scroller so they stay put while the plot moves. */}
      <div style={{ ...titleStyle, top: 0, lineHeight: `${TITLE_H}px` }}>{priceTitle || tr("widget.priceDaily")}</div>
      <div style={{ ...titleStyle, top: sTitleTop, lineHeight: `${STITLE_H}px` }}>
        {tr("widget.sentimentHistory")}
        <span style={{ color: "#3b82f6", marginLeft: 10 }}>—&nbsp;{tr("widget.trend")}</span>
        <span style={{ color: "#f97316", marginLeft: 8 }}>—&nbsp;{tr("widget.momentum")}</span>
      </div>

      {scrolls && (
        <div className="sw-scrollbar" onMouseDown={jumpTo}
             style={{ position: "absolute", left: AXIS_W, right: MR,
                      top: axisTop + AXIS_H + 4 }}>
          <div className="sw-scrollbar-thumb"
               onMouseDown={e => { e.stopPropagation(); startDrag(e); }}
               style={{ width: `${thumbPct}%`, marginLeft: `${thumbPos}%` }} />
        </div>
      )}

      <div style={{ display: "flex", alignItems: "flex-start" }}>
        {/* Pinned Y axes */}
        <svg width={AXIS_W} height={totalH}
             style={{ display: "block", flex: "0 0 auto" }}>
          {pTicks.map((p, i) => (
            <text key={`p${i}`} x={AXIS_W - 4} y={(pY(p) + 3.5).toFixed(1)}
                  textAnchor="end" fill="#6b7280" fontSize={9}>{fmtPrice(p)}</text>
          ))}
          {sTicks.map((s, i) => (
            <text key={`s${i}`} x={AXIS_W - 4} y={(sY(s) + 3.5).toFixed(1)}
                  textAnchor="end" fill="#6b7280" fontSize={9}>{s.toFixed(2)}</text>
          ))}
          <line x1={AXIS_W - 0.5} y1={candleTop} x2={AXIS_W - 0.5} y2={axisTop}
                stroke="#9ca3af" strokeWidth={1} />
          <line x1={AXIS_W - 0.5} y1={sentTop} x2={AXIS_W - 0.5} y2={sentTop + SENT_H}
                stroke="#9ca3af" strokeWidth={1} />
        </svg>

        {/* Scrollable plot — price and sentiment share this SVG and its x scale */}
        <div ref={scrollRef} className="sw-chart-scroll"
             onScroll={e => setScrollX(e.currentTarget.scrollLeft)}
             style={{ overflowX: scrolls ? "auto" : "hidden", overflowY: "hidden",
                      flex: "1 1 auto", minWidth: 0 }}>
          <svg width={plotW} height={totalH}
               style={{ display: "block", cursor: "crosshair" }}
               onMouseMove={handleMouseMove} onMouseLeave={() => setHoverIdx(null)}>

            <defs>
              <filter id={glowId} x="-120%" y="-120%" width="340%" height="340%">
                <feDropShadow dx="0" dy="0" stdDeviation="2.6" floodColor="#6366f1"
                              floodOpacity="0.85" />
              </filter>
              {/* With the price axis scaled to the visible window, off-screen
                  bars get y-positions far outside the pane; while scrolling
                  they can sweep through the sentiment pane below. Everything
                  price-anchored renders inside this clip. */}
              <clipPath id={clipId}>
                <rect x={0} y={candleTop - 2} width={R}
                      height={sTitleTop - candleTop} />
              </clipPath>
            </defs>

            {pTicks.map((p, i) => (
              <line key={i} x1={0} y1={pY(p).toFixed(1)} x2={R} y2={pY(p).toFixed(1)}
                    stroke="#e5e7eb" strokeWidth={1} />
            ))}

            <g clipPath={`url(#${clipId})`}>
            {allDates.map((date, i) => {
              const c = candleMap[date];
              if (!c || date === TODAY) return null;
              const x   = xOf(i);
              const col = c.close >= c.open ? "#22c55e" : "#ef4444";
              const bt  = pY(Math.max(c.open, c.close));
              const bb  = pY(Math.min(c.open, c.close));
              return (
                <g key={date}>
                  <line x1={x.toFixed(1)} y1={pY(c.high).toFixed(1)}
                        x2={x.toFixed(1)} y2={pY(c.low).toFixed(1)}
                        stroke={col} strokeWidth={1} />
                  <rect x={(x - barW / 2).toFixed(1)} y={bt.toFixed(1)}
                        width={barW.toFixed(1)} height={Math.max(bb - bt, 1).toFixed(1)}
                        fill={col} />
                </g>
              );
            })}

            {/* Signal markers — triangles above/below candles */}
            {allDates.map((date, i) => {
              const c = candleMap[date];
              const h = histMap[date];
              if (!h) return null;
              // The newest scored bar is the forward-looking one: on D1 its
              // candle has not closed yet, so it previously rendered nothing at
              // all. It gets drawn like any other marker, with a glow.
              const isLatest = i === latestScoredIdx;
              const x  = xOf(i);
              const tw = Math.min(5, Math.max(3, barW * 0.6));
              const th = tw + 1;
              // With no candle there is no high/low to sit against — anchor to
              // the last known close so it reads as "from here, this direction"
              // rather than pinning to the top or bottom of the pane.
              const anchor = lastClose != null ? pY(lastClose) : candleTop + CANDLE_H / 2;
              const bullBase = c ? pY(c.low)  + 10 : anchor + 10;
              const bearBase = c ? pY(c.high) - 10 : anchor - 10;
              const tri = (dir, base, col, slot) => {
                const by = dir === "up"   ? base + slot * 13 : base - slot * 13;
                const pts = dir === "up"
                  ? `${x},${by - th} ${x - tw},${by} ${x + tw},${by}`
                  : `${x},${by + th} ${x - tw},${by} ${x + tw},${by}`;
                return <polygon key={`${dir}${slot}`} points={pts} fill={col}
                                className={isLatest ? "sw-live-marker" : undefined}
                                filter={isLatest ? `url(#${glowId})` : undefined}
                                opacity={selectedIdea && !isLatest ? 0.18 : 0.9} />;
              };
              const d7bull = h.score_d7 >  thresh;
              const d7bear = h.score_d7 < -thresh;
              const d3bull = h.score_d3 >  thresh;
              const d3bear = h.score_d3 < -thresh;
              const els = [];
              if (d7bull) els.push(tri("up",   bullBase, "#3b82f6", 0));
              if (d7bear) els.push(tri("down", bearBase, "#3b82f6", 0));
              if (d3bull) els.push(tri("up",   bullBase, "#f97316", d7bull ? 1 : 0));
              if (d3bear) els.push(tri("down", bearBase, "#f97316", d7bear ? 1 : 0));
              return els.length ? <g key={date}>{els}</g> : null;
            })}

            {/* Trades draw LAST so nothing paints over them — the signal
                triangles above were doing exactly that. */}
            <TradeOverlay trades={selectedIdea?.trades} dateIndex={dateIndex}
                          xOf={xOf} pY={pY} lastClose={lastClose} rightEdge={R}
                          onTradeHover={setHoverTrade} />
            {tradeTipEl}

            </g>

            {/* ── Shared x-axis ── */}
            <line x1={0} y1={axisTop} x2={R} y2={axisTop} stroke="#9ca3af" strokeWidth={1} />
            {xTicks.map(i => (
              <text key={i} x={xOf(i).toFixed(1)} y={axisTop + 14}
                    textAnchor="middle" fill="#6b7280" fontSize={9}>
                {xLabel(allDates[i])}
              </text>
            ))}

            {/* Live marker: the bar the newest score belongs to.
                The glow is a wide translucent line underneath, NOT an SVG
                filter. A perfectly vertical line has a zero-width bounding
                box, and filter regions are percentages of that box — so the
                region collapses to nothing and the element paints nothing at
                all, while still reporting opacity 1 in the DOM. */}
            {latestScoredIdx >= 0 && (() => {
              // With a prediction slot the marker sits in the gutter, on the
              // prediction triangle. Without one the newest scored bar IS a
              // candle, and marking its centre draws the line straight through
              // the body (US500 H4 overlapped by 4px) — so sit on that slot's
              // trailing edge instead: still "here is the newest score", but a
              // boundary rather than a strike-through.
              const mx = (hasPrediction
                ? xOf(latestScoredIdx)
                : xOf(latestScoredIdx) + slotW / 2).toFixed(1);
              return (
                <g className="sw-live-marker">
                  <line x1={mx} y1={candleTop} x2={mx} y2={sentTop + SENT_H}
                        stroke="#6366f1" strokeWidth={8} opacity={0.16}
                        strokeLinecap="round" />
                  <line x1={mx} y1={candleTop} x2={mx} y2={sentTop + SENT_H}
                        stroke="#6366f1" strokeWidth={1.6} strokeDasharray="4 3" />
                </g>
              );
            })()}

            {sMin < thresh && sMax > thresh && (
              <line x1={0} y1={sY(thresh).toFixed(1)} x2={R} y2={sY(thresh).toFixed(1)}
                    stroke="#22c55e40" strokeWidth={1} strokeDasharray="3 3" />
            )}
            {sMin < 0 && sMax > 0 && (
              <line x1={0} y1={sY(0).toFixed(1)} x2={R} y2={sY(0).toFixed(1)}
                    stroke="#d1d5db" strokeWidth={1} />
            )}
            {sMin < -thresh && sMax > -thresh && (
              <line x1={0} y1={sY(-thresh).toFixed(1)} x2={R} y2={sY(-thresh).toFixed(1)}
                    stroke="#ef444440" strokeWidth={1} strokeDasharray="3 3" />
            )}

            <path d={d7Path} fill="none" stroke="#3b82f6" strokeWidth={1.5} />
            <path d={d3Path} fill="none" stroke="#f97316" strokeWidth={1.5} />

            {/* Tooltip + crosshair rendered last (on top) */}
            {ttipEl}

          </svg>
        </div>
      </div>
    </div>
  );
}

// ── Main widget ───────────────────────────────────────────────────────────────

function fmtLocalTs(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString([], { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

/** Axis label formatter for an intraday series, in the viewer's LOCAL zone.
 *
 *  Chosen from the span the chart actually covers, not from the timeframe:
 *  labels are drawn every Nth bar, and for 180 H4 bars that step lands exactly
 *  48h apart — so a clock-only label printed the same time for every tick and
 *  the axis said nothing. Over a few days or more the date identifies a bar;
 *  within a day or two, the time does.
 */
function makeIntradayLabel(points) {
  const spanDays = points.length > 1
    ? (new Date(points[points.length - 1].t) - new Date(points[0].t)) / 86400000
    : 0;
  const dateOnly = spanDays > 3;
  return (iso) => {
    const d = new Date(iso);
    if (isNaN(d)) return String(iso).slice(5, 10);
    if (dateOnly) return d.toLocaleDateString([], { month: "2-digit", day: "2-digit" });
    // Within a short span show the time, but mark local midnight with the date
    // so day boundaries stay readable.
    if (d.getHours() === 0) {
      return d.toLocaleDateString([], { month: "2-digit", day: "2-digit" });
    }
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };
}

function SentimentWidget({ data, intraday = null, thresh = 0.15, selectedIdea = null }) {
  const { t } = useTranslation();
  if (!data?.latest) return null;

  const { latest, previous, history, candles } = data;
  const trendName = t("widget.newsTrend");
  const momName   = t("widget.newsMomentum");

  const g = intraday?.gauges;
  const s = intraday?.series;

  // Assemble the timeframes that actually have data. A timeframe with no
  // in-window articles is omitted entirely: a 0.000 NEUTRAL gauge reads as a
  // signal when it is really an absence.
  const tfs = [];
  for (const [tf, titleKey] of [["H1", "widget.tfH1"], ["H4", "widget.tfH4"]]) {
    const pts = s?.[tf];
    if (!g?.[tf] || !pts || pts.length < 2 || !(g[tf].trend?.n > 0)) continue;
    const prev = pts[pts.length - 2];
    // Show only the span the sentiment series covers, plus a little price
    // context before it. Hourly scoring is new: H1 has 168 candles but only
    // hours of scores, so an untrimmed chart is a full price pane over a
    // sentiment pane that is ~90% void with the line huddled at the right
    // edge — which reads as broken, not as "history still accumulating".
    // The window grows with the series until it reaches the full candle set.
    const CONTEXT_BARS = 10;
    const firstScored = pts[0].t;
    const allC = intraday.candles?.[tf] || [];
    let from = allC.findIndex(b => b.t >= firstScored);
    if (from < 0) from = Math.max(0, allC.length - CONTEXT_BARS);
    from = Math.max(0, from - CONTEXT_BARS);
    tfs.push({
      tf, title: t(titleKey),
      trend: g[tf].trend, momentum: g[tf].momentum,
      prevTrend: prev?.trend, prevMom: prev?.mom,
      candles: allC.slice(from).map(b => ({
        date: b.t, open: b.o, high: b.h, low: b.l, close: b.c })),
      history: pts.map(p => ({ date: p.t, score_d7: p.trend, score_d3: p.mom })),
      xLabel: makeIntradayLabel(pts),
      // Strategy trades are backtested on daily bars only, so the overlay
      // belongs to D1 and would be meaningless plotted on an intraday chart.
      selectedIdea: null,
    });
  }

  // Daily: gauges refresh hourly from the intraday doc when present, otherwise
  // fall back to the daily payload exactly as before.
  tfs.push({
    tf: "D1", title: t("widget.tfD1"),
    trend: g?.D1?.trend ?? { score: latest.score_d7, label: latest.label_d7, confidence: latest.confidence_d7 },
    momentum: g?.D1?.momentum ?? { score: latest.score_d3, label: latest.label_d3, confidence: latest.confidence_d3 },
    prevTrend: previous?.score_d7, prevMom: previous?.score_d3,
    candles: candles || [], history: history || [],
    xLabel: (d) => String(d).slice(5),
    selectedIdea,
  });

  const updatedAt = intraday?.generated_at || latest.date;

  return (
    <div className="sw-container">
      {/* One tile per timeframe: its two gauges and their deltas read as a
          single unit, instead of six unlabelled gauges in a row where the
          reader has to work out which pair belongs together. */}
      <div className="sw-tf-row">
        {tfs.map(x => (
          <div className="sw-tf-tile" key={x.tf}>
            <div className="sw-tf-tile-title">{x.title}</div>
            <div className="sw-gauges-row">
              <Gauge score={x.trend.score} label={x.trend.label}
                     confidence={x.trend.confidence} windowName={trendName} />
              <Gauge score={x.momentum.score} label={x.momentum.label}
                     confidence={x.momentum.confidence} windowName={momName} />
            </div>
            <div className="sw-direction-row">
              <DirectionIndicator current={x.trend.score} previous={x.prevTrend}
                                  windowName={trendName} />
              <DirectionIndicator current={x.momentum.score} previous={x.prevMom}
                                  windowName={momName} />
            </div>
          </div>
        ))}
      </div>

      <div className="sw-updated">{t("widget.updated")} {fmtLocalTs(updatedAt)}</div>

      {/* Charts stacked, one per timeframe */}
      {tfs.map(x => (
        (x.candles.length > 0 && x.history.length > 1) ? (
          <div className="sw-chart-block" key={x.tf}>
            <div className="sw-chart-label">{x.title}</div>
            <div className="sw-chart-section">
              <CombinedChart candles={x.candles} history={x.history} thresh={thresh}
                             selectedIdea={x.selectedIdea} xLabel={x.xLabel}
                             priceTitle={x.tf === "D1" ? null : t("widget.priceTf", { tf: x.tf })}
                             slotDivisor={x.tf === "D1" ? null : (candles?.length || 0) + 1} />
            </div>
          </div>
        ) : null
      ))}
    </div>
  );
}

export default SentimentWidget;
